import { fmtNum, fmtTimeNL } from '@/features/ah/utils';
import type { PrijsfavorietenPayload } from '../report';
import { calendarWeekNumbers, fmtDecimal, partStatus } from '../utils';

function KPI({
  label,
  value,
  sub,
  cadence,
  split,
}: {
  label: string;
  value: string;
  sub?: string;
  cadence?: 'live' | 'daily';
  split?: { label: string; value: string }[];
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">
        {label}
        {cadence && <span className={'cadence-badge ' + cadence}>{cadence === 'live' ? 'real-time' : 'dagelijks'}</span>}
      </div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-sub">{sub && <span>{sub}</span>}</div>
      {split && split.length > 0 && (
        <div className="kpi-split">
          {split.map((s) => (
            <div key={s.label} className="kpi-split-item">
              <span className="kpi-split-k">{s.label}</span>
              <span className="kpi-split-v">{s.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Overzicht KPIs: four headline tiles plus games-played with a per-period split. */
export function KPIGrid({ data, scope }: { data: PrijsfavorietenPayload; scope?: string }) {
  const t = data.totals;
  // In the two-part layout the tiles are ONE part's totals, and say so.
  const soFar = data.campaign.live ? ' tot nu toe' : '';
  const playersSub = scope ? `totaal in ${scope}${soFar}` : 'totaal over de hele campagne';
  const visitsSub = scope ? `sessies in ${scope}${soFar}` : 'sessies van unieke bezoekers';
  return (
    <>
      <div className="kpi-grid">
        <KPI label="Totaal unieke bezoekers" value={fmtNum(t.uniqueVisitors)} sub={`gem. ${fmtNum(t.avgVisitorsPerDay)} / dag`} cadence="live" />
        <KPI label="Totaal unieke spelers" value={fmtNum(t.uniquePlayers)} sub={playersSub} cadence="live" />
        <KPI label="Totaal aantal bezoeken" value={fmtNum(t.visits)} sub={visitsSub} cadence="live" />
        <KPI label="Totale tijd in campagne" value={fmtTimeNL(t.timeInCampaign)} sub={`gem. ${fmtTimeNL(t.avgTimePerUser)} / gebruiker`} cadence="daily" />
      </div>
      <div className="kpi-grid kpi-grid-row2">
        {/* Per-week figures moved to the "Overzicht per week" table, so this
            tile is the campaign total only (design update, 21 Aug). */}
        <KPI label="Totaal games gespeeld" value={fmtNum(t.gamesPlayed)} sub={`gem. ${fmtDecimal(t.avgGamesPerPlayer)} per speler`} cadence="live" />
      </div>
    </>
  );
}

/**
 * "Deel 2 · CW 40 · Lopend — Totalen": which part the tiles below add up, and
 * where it stands (the running calendar week while live, all its weeks after).
 */
export function PartTotalsHead({ campaign }: { campaign: PrijsfavorietenPayload['campaign'] }) {
  const status = partStatus(campaign);
  const cw = calendarWeekNumbers(campaign.calendarWeeks)[campaign.currentWeek - 1];
  const when = status === 'Lopend' && cw != null ? `CW ${cw}` : campaign.calendarWeeks;
  return (
    <div className="pf-totals-head">
      <span className="pf-totals-pill">
        {campaign.periodLabel} · {when} · {status}
      </span>
      <h2>Totalen</h2>
    </div>
  );
}
